import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowsLeftRight, Lightning, ArrowRight, Trophy, Fire } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { playerToSlug } from "@/lib/playerSlug";

const PC = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24", PICK: "#ccff00" };
const VERDICT_COLORS = {
  "EVEN": { bg: "bg-[#fbbf24]/10", border: "border-[#fbbf24]/30", text: "text-[#fbbf24]" },
  "CLOSE": { bg: "bg-[#4ade80]/10", border: "border-[#4ade80]/30", text: "text-[#4ade80]" },
  "SLIGHT WIN": { bg: "bg-[#45b7d1]/10", border: "border-[#45b7d1]/30", text: "text-[#45b7d1]" },
  "CLEAR WIN": { bg: "bg-[#ff6b6b]/10", border: "border-[#ff6b6b]/30", text: "text-[#ff6b6b]" },
  "LOPSIDED": { bg: "bg-[#ff3b30]/10", border: "border-[#ff3b30]/30", text: "text-[#ff3b30]" },
  "LOPSIDED+": { bg: "bg-[#ff6b6b]/20", border: "border-[#ff6b6b]/50", text: "text-[#ff6b6b]" },
  "VERY LOPSIDED": { bg: "bg-[#ff3b30]/20", border: "border-[#ff3b30]/50", text: "text-[#ff3b30]" },
  "EXTREME OUTLIER": { bg: "bg-[#ff3b30]/30", border: "border-[#ff3b30]/70", text: "text-[#ff3b30]" },
};

function GradeCard({ trade, index }) {
  const vc = VERDICT_COLORS[trade.verdict] || VERDICT_COLORS["CLOSE"];
  const winSide = trade.winner === "a" ? "A" : "B";

  return (
    <div className="border border-[#262626] bg-[#171717] p-5 rounded-sm" data-testid={`graded-trade-${index}`}>
      {/* Verdict badge */}
      <div className="flex items-center justify-between mb-4">
        <div className={`px-3 py-1 border rounded-sm ${vc.bg} ${vc.border}`}>
          <span className={`text-xs font-bold uppercase ${vc.text}`}>{trade.verdict}</span>
          {trade.verdict !== "EVEN" && <span className="text-[10px] text-[#888] ml-1.5">Side {winSide} wins by {trade.gap_pct}%</span>}
          {trade.percentile >= 90 && <span className="text-[9px] text-[#ff3b30] ml-1.5">Top {(100 - trade.percentile).toFixed(1)}% most lopsided</span>}
        </div>
        {trade.week > 0 && <span className="text-[9px] text-[#555]">Week {trade.week}</span>}
      </div>

      {/* Two sides */}
      <div className="grid grid-cols-2 gap-4">
        <div className={`p-3 border rounded-sm ${trade.winner === "a" ? "border-[#ccff00]/30 bg-[#ccff00]/5" : "border-[#262626]"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase text-[#555]">SIDE A</span>
            <span className="text-xs font-mono font-bold text-white">{trade.value_a.toLocaleString()}</span>
          </div>
          <div className="space-y-1.5">
            {trade.side_a.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[9px] font-bold px-1 rounded-sm" style={{ color: PC[p.position], backgroundColor: PC[p.position] + "15" }}>{p.position}</span>
                  {p.position !== "PICK" ? (
                    <Link to={`/players/${p.slug}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
                  ) : (
                    <span className="text-xs text-white">{p.name}</span>
                  )}
                </div>
                <span className="text-[10px] font-mono text-[#888] shrink-0 ml-2">{p.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`p-3 border rounded-sm ${trade.winner === "b" ? "border-[#ccff00]/30 bg-[#ccff00]/5" : "border-[#262626]"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase text-[#555]">SIDE B</span>
            <span className="text-xs font-mono font-bold text-white">{trade.value_b.toLocaleString()}</span>
          </div>
          <div className="space-y-1.5">
            {trade.side_b.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[9px] font-bold px-1 rounded-sm" style={{ color: PC[p.position], backgroundColor: PC[p.position] + "15" }}>{p.position}</span>
                  {p.position !== "PICK" ? (
                    <Link to={`/players/${p.slug}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
                  ) : (
                    <span className="text-xs text-white">{p.name}</span>
                  )}
                </div>
                <span className="text-[10px] font-mono text-[#888] shrink-0 ml-2">{p.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GradedTrades() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/trades/graded?limit=12&filter=${filter}`);
        setTrades(res.data.trades || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [filter]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Graded Dynasty Trades: Real Deals Scored by Value — Dynasty Trade Maker" description="Real dynasty trades from Sleeper leagues, graded by total value on each side. See which side won, by how much, and find fair vs lopsided deals." path="/trades/graded" />
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">REAL TRADES, REAL VALUES</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2">GRADED <span className="text-[#ccff00]">TRADES</span></h1>
        <p className="text-sm text-[#a3a3a3] mb-6">Real dynasty trades from Sleeper leagues with FantasyCalc values assigned to each side. See who won.</p>

        <div className="flex gap-2 mb-6">
          {[
            { val: "all", label: "All Trades" },
            { val: "close", label: "Close Calls" },
            { val: "fair", label: "Fair Deals" },
            { val: "lopsided", label: "Lopsided" },
            { val: "collusion", label: "Extreme Outliers" },
          ].map(f => (
            <button key={f.val} onClick={() => setFilter(f.val)} className={`px-3 py-1.5 text-[10px] font-bold uppercase rounded-sm transition-colors ${filter === f.val ? "bg-[#ccff00] text-black" : "border border-[#262626] text-[#888] hover:border-[#444]"}`} data-testid={`filter-${f.val}`}>{f.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">{[...Array(3)].map((_,i) => <div key={i} className="h-40 skeleton rounded-sm" />)}</div>
        ) : (
          <div className="space-y-4">
            {trades.map((t, i) => <GradeCard key={i} trade={t} index={i} />)}
            {trades.length === 0 && <p className="text-center text-[#555] py-8">No trades match this filter</p>}
          </div>
        )}

        <div className="mt-8 border border-[#ccff00]/30 bg-[#ccff00]/5 p-5 text-center">
          <h3 className="font-heading text-lg text-white uppercase mb-2">CHECK YOUR OWN TRADE</h3>
          <p className="text-xs text-[#888] mb-3">Use the Trade Fairness Analyzer to grade any trade against our 125K-trade dataset, or the Trade Calculator for quick value comparisons.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/tools/trade-fairness" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold">TRADE FAIRNESS ANALYZER</Link>
            <Link to="/trade-calculator" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Trade Calculator <ArrowRight size={11} /></Link>
          </div>
        </div>

        <div className="mt-6 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/trades" className="hover:text-[#a3a3a3] no-underline">Trade Database</Link>
          <Link to="/weekly/trades" className="hover:text-[#a3a3a3] no-underline">Weekly Trades</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
        </div>
      </div>
    </div>
  );
}
